import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import React, { FC, useMemo, useState } from 'react';
import { formatDateString, formatEpoch, formatMonth, Table } from '../../shared';
import { useReportResult } from '../hooks/use-report-result.hook';
import { ColumnType } from '../models/column.model';
import { IReportResult } from '../models/report-result.model';
import { IReport } from '../models/report.model';
import { IRunReportFilter } from '../models/run-report.model';
import { allRequiredFiltersFilledInCheck } from '../utils/all-required-filters-filled-in-check.util';

interface Props {
    report: IReport;
    filters: IRunReportFilter[];
}

const TOTAL_ROW_ID = -1;

export const Report: FC<Props> = ({ report, filters }) => {
    const [sortModel, setSortModel] = useState<GridSortModel>();

    const columns = useChartColumns(report);

    const reportFilters = useFilterOutPreviousReportFilters(filters, report);

    const { data: reportResult, isLoading } = useReportResult(
        report.id,
        reportFilters,
        sortModel?.[0] && {
            column: sortModel[0].field,
            order: (sortModel[0].sort?.toUpperCase() as 'ASC') || 'ASC',
        },
        allRequiredFiltersFilledInCheck(report.filters, filters),
    );
    const rows = useChartRows(reportResult);

    return (
        <Table
            key={report.id}
            prefix={`report-${report.id}`}
            getRowId={({ rowId }) => rowId}
            getRowClassName={({ id }) => (id === TOTAL_ROW_ID ? 'report-total-row' : '')}
            rows={rows}
            columns={columns}
            loading={isLoading}
            paginationMode="client"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
            disableColumnMenu={false}
            sx={{ '.report-total-row': { fontWeight: 'bold' } }}
        />
    );
};

function useChartRows(reportResult: IReportResult | undefined) {
    return useMemo(() => {
        const { data = [], totals } = reportResult || {};
        const hasTotals = totals !== undefined && Object.entries(totals)?.length > 0;
        return hasTotals && data.length > 0 ? [...data, hasTotals && { ...totals, rowId: TOTAL_ROW_ID }] : [...data];
    }, [reportResult]);
}

function useChartColumns(report: IReport | undefined): GridColDef[] {
    return useMemo(
        () =>
            report?.columns
                .filter(({ hidden }) => !hidden)
                .map((column, index) => ({
                    headerName: column.label,
                    field: column.name,
                    flex: 1,
                    hideable: index !== 0,
                    minWidth: 150,
                    valueFormatter: (value) => formatTableValue(value, column.type),
                })) || [],
        [report?.columns],
    );
}

function formatTableValue(value: unknown, type: ColumnType): unknown {
    switch (type) {
        case ColumnType.DATE:
            return formatDateString(value as string);
        case ColumnType.DATETIME:
            return formatEpoch(value as number);
        case ColumnType.MONTH:
            return formatMonth(value as number);
        default:
            return value;
    }
}

function useFilterOutPreviousReportFilters(filters: IRunReportFilter[], chart: IReport) {
    return useMemo(
        () =>
            filters.filter((filter) =>
                chart.filters.some(
                    (chartFilter) => chartFilter.type === filter.type && chartFilter.column === filter.column,
                ),
            ),
        [filters, chart],
    );
}
